import { useTranslation } from "react-i18next";
import { Checkbox, Icon } from "semantic-ui-react";

import { DetailsCard, useCan } from "components";
import { setShowPriceInVat, useShowPriceInVat } from "hooks";
import { STATUS_IDENTIFIERS } from "models";
import { CustomerCommunicationInterventionItem } from "modules/AppointmentDetails/components/CustomerCommunicationModal/components/CustomerCommunicationInterventions/components";
import "modules/AppointmentDetails/components/CustomerCommunicationModal/components/CustomerCommunicationInterventions/CustomerCommunicationInterventions.scss";
import { useUpdateInterventionMutation } from "modules/AppointmentDetails/hooks";

import type { Appointment, Intervention } from "models";

type CustomerCommunicationInterventionsProps = {
  appointment: Appointment;
  invalidContent?: boolean;
  showCustomerOk?: boolean;
  showVat?: boolean;
  onSwitchShowVat?: () => void;
};

export const CustomerCommunicationInterventions = ({
  appointment,
  invalidContent,
  showCustomerOk,
  showVat,
  onSwitchShowVat
}: CustomerCommunicationInterventionsProps) => {
  const { t } = useTranslation();

  const showPriceInVat = useShowPriceInVat();
  const canUpdateInterventions = useCan("update", "appointments");
  const { mutate: updateIntervention } = useUpdateInterventionMutation();

  const isVatShown = showVat ?? showPriceInVat;
  const switchShowVat = onSwitchShowVat ?? (() => setShowPriceInVat(!showPriceInVat));

  if (!appointment.interventions?.length) return null;

  const toggleVisible = (intervention: Intervention) => {
    if (!canUpdateInterventions || appointment.appointment_status_identifier === STATUS_IDENTIFIERS.CanceledStatus) return;

    updateIntervention({
      ...intervention,
      visible_to_customer: !intervention.visible_to_customer
    });
  };

  const toggleCustomerOk = (intervention: Intervention) => {
    if (!showCustomerOk || appointment.appointment_status_identifier === STATUS_IDENTIFIERS.CanceledStatus) return;

    updateIntervention({
      ...intervention,
      customer_ok: !intervention.customer_ok
    });
  };

  const approvedInterventions = appointment.interventions.filter(intervention => intervention.customer_ok);
  const pendingInterventions = appointment.interventions.filter(intervention => !intervention.customer_ok);

  return (
    <DetailsCard
      title={`${t("v8_interventions", "Interventions")} ${appointment.interventions.length ? `(${appointment.interventions.length})` : ""}`}
      icon="screwdriver wrench"
      isExpanded={true}
      showExpandArrow={false}
      containerStyle={invalidContent && !appointment.interventions.some(i => i.visible_to_customer) ? "error" : ""}
      rightContent={
        <div className="full end">
          <p>{t("v8_show_prices_with_vat", "Show prices with VAT")}</p>
          <Checkbox toggle checked={isVatShown} onChange={switchShowVat} />
        </div>
      }
    >
      <div className="CustomerCommunicationInterventions">
        <div className="CustomerCommunicationInterventions-container">
          {!!pendingInterventions.length && (
            <div className="CustomerCommunicationInterventions-section">
              <div className="CustomerCommunicationInterventions-section-group">
                <div className="CustomerCommunicationInterventions-section-group_header">
                  <Icon className="crossIcon circle xmark outline red" />
                  <p>{t("v8_approval_pending", "Approval Pending")}</p>
                </div>
                {pendingInterventions.map((item: Intervention) => {
                  return (
                    <CustomerCommunicationInterventionItem
                      key={item.id}
                      intervention={item}
                      toggleVisible={toggleVisible}
                      showVat={isVatShown}
                      {...(showCustomerOk && { toggleCustomerOk })}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {!!approvedInterventions.length && (
            <div className="CustomerCommunicationInterventions-section">
              <div className="CustomerCommunicationInterventions-section-group">
                <div className="CustomerCommunicationInterventions-section-group_header">
                  <Icon className="check circle outline green" />
                  <p>{t("v8_approved", "Approved")}</p>
                </div>
                {approvedInterventions.map((item: Intervention) => {
                  return (
                    <CustomerCommunicationInterventionItem
                      key={item.id}
                      intervention={item}
                      toggleVisible={toggleVisible}
                      showVat={isVatShown}
                      {...(showCustomerOk && { toggleCustomerOk })}
                    />
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </DetailsCard>
  );
};
